import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import Typography from "@material-ui/core/Typography";
import {
  Button,
  IconButton,
  StepContent,
  TextField,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SaveIcon from "@material-ui/icons/Save";
import EditIcon from "@material-ui/icons/Edit";
import api, { coreApi } from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "20vh",
    },
  },
  button: {
    marginRight: theme.spacing(1),
  },
  input: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  addButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

function updateNodeAtPath(nodes, path, updater) {
  if (!Array.isArray(path) || path.length === 0) {
    return updater(nodes);
  }

  const [idx, ...rest] = path;
  return nodes.map((n, i) => {
    if (i !== idx) return n;
    if (rest.length === 0) {
      return updater(n);
    }
    const children = Array.isArray(n.children) ? n.children : [];
    return {
      ...n,
      children: updateNodeAtPath(children, rest, updater),
    };
  });
}

function removeNodeAtPath(nodes, path) {
  if (!Array.isArray(path) || path.length === 0) return nodes;
  const [idx, ...rest] = path;
  if (rest.length === 0) {
    return [...nodes.slice(0, idx), ...nodes.slice(idx + 1)];
  }
  return nodes.map((n, i) => {
    if (i !== idx) return n;
    const children = Array.isArray(n.children) ? n.children : [];
    return {
      ...n,
      children: removeNodeAtPath(children, rest),
    };
  });
}

function getNodeAtPath(nodes, path) {
  if (!Array.isArray(path) || path.length === 0) return undefined;
  let cur = nodes;
  let node;
  for (let i = 0; i < path.length; i++) {
    const idx = path[i];
    if (!cur || !Array.isArray(cur) || idx < 0 || idx >= cur.length) {
      return undefined;
    }
    node = cur[idx];
    cur = node.children || [];
  }
  return node;
}

function OptionChildrenFetcher({ queueId, parentId, parentPath, onReplaceChildren }) {
  const { data = [], isFetching } = coreApi["queue-options"].useList(
    { queueId, parentId },
    {
      enabled: !!queueId && !!parentId,
      select: (arr) =>
        (Array.isArray(arr) ? arr : []).map((option) => ({
          ...option,
          children: option.children || [],
          edition: false,
        })),
    }
  );

  useEffect(() => {
    if (!parentId) return;
    onReplaceChildren(parentPath, data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data), parentId]);

  return null;
}

export function QueueOptionStepper({
  queueId,
  options = [],
  pathPrefix = [],
  onAddChild,
  onToggleEdition,
  onChangeField,
  onDelete,
  onReplaceChildren,
}) {
  const classes = useStyles();
  const [activeOption, setActiveOption] = useState(-1);

  const createMutation = coreApi["queue-options"].useCreate();
  const updateMutation = coreApi["queue-options"].useUpdate();

  const handleOptionClick = (index) => () => {
    setActiveOption(index);
  };

  const handleSaveLocal = async (nodePath) => {
    const option = getNodeAtPath(options, nodePath.slice(pathPrefix.length));
    if (!option) return;
    try {
      if (option.id) {
        // update
        if (updateMutation && updateMutation.mutateAsync) {
          await updateMutation.mutateAsync({ id: option.id, ...option });
        } else if (updateMutation && updateMutation.mutate) {
          await new Promise((res, rej) =>
            updateMutation.mutate({ id: option.id, ...option }, { onSuccess: res, onError: rej })
          );
        }
      } else {
        if (createMutation && createMutation.mutateAsync) {
          const data = await createMutation.mutateAsync(option);
          if (data && data.id) {
            onChangeField(nodePath, "id", data.id);
          }
        } else if (createMutation && createMutation.mutate) {
          await new Promise((res, rej) =>
            createMutation.mutate(option, {
              onSuccess: (data) => {
                if (data && data.id) onChangeField(nodePath, "id", data.id);
                res(data);
              },
              onError: rej,
            })
          );
        }
      }
      onToggleEdition(nodePath, false);
    } catch (e) {
      toastError(e);
    }
  };

  const renderTitle = (option, index, nodePath) => {
    if (option.edition) {
      return (
        <>
          <TextField
            value={option.title}
            onChange={(e) => onChangeField(nodePath, "title", e.target.value)}
            size="small"
            className={classes.input}
            placeholder="Título de la opción"
          />
          <>
            <IconButton
              color="primary"
              variant="outlined"
              size="small"
              className={classes.button}
              onClick={() => handleSaveLocal(nodePath)}
            >
              <SaveIcon />
            </IconButton>
            <IconButton
              variant="outlined"
              color="secondary"
              size="small"
              className={classes.button}
              onClick={() => onDelete(nodePath)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </>
        </>
      );
    }
    return (
      <>
        <Typography>
          {option.title !== "" ? option.title : "Titulo no definido"}
          <IconButton
            variant="outlined"
            size="small"
            className={classes.button}
            onClick={() => onToggleEdition(nodePath, !option.edition)}
          >
            <EditIcon />
          </IconButton>
        </Typography>
      </>
    );
  };

  const renderMessage = (option, nodePath) => {
    if (option.edition) {
      return (
        <TextField
          style={{ width: "100%" }}
          multiline
          value={option.message}
          onChange={(e) => onChangeField(nodePath, "message", e.target.value)}
          size="small"
          className={classes.input}
          placeholder="Introduzca el mensaje de la opción"
        />
      );
    }
    return (
      <Typography onClick={() => onToggleEdition(nodePath, true)}>
        {option.message}
      </Typography>
    );
  };

  const renderStep = (option, index) => {
    const nodePath = [...pathPrefix, index];
    return (
      <Step key={index}>
        <StepLabel style={{ cursor: "pointer" }} onClick={handleOptionClick(index)}>
          {renderTitle(option, index, nodePath)}
        </StepLabel>
        <StepContent>
          {renderMessage(option, nodePath)}

          {option.id !== undefined && (
            <Button
              color="primary"
              size="small"
              onClick={() => onAddChild(nodePath, null, { replace: false })}
              startIcon={<AddIcon />}
              variant="outlined"
              className={classes.addButton}
            >
              Añadir
            </Button>
          )}

          {option.id ? (
            <OptionChildrenFetcher
              queueId={queueId}
              parentId={option.id}
              parentPath={nodePath}
              onReplaceChildren={onReplaceChildren}
            />
          ) : null}

          <QueueOptionStepper
            queueId={queueId}
            options={option.children || []}
            pathPrefix={nodePath}
            onAddChild={onAddChild}
            onToggleEdition={onToggleEdition}
            onChangeField={onChangeField}
            onDelete={onDelete}
            onReplaceChildren={onReplaceChildren}
          />
        </StepContent>
      </Step>
    );
  };

  return (
    <Stepper
      style={{ marginBottom: 0, paddingBottom: 0 }}
      nonLinear
      activeStep={activeOption}
      orientation="vertical"
    >
      {Array.isArray(options) && options.map((o, i) => renderStep(o, i))}
    </Stepper>
  );
}

export function QueueOptions({ queueId }) {
  const classes = useStyles();
  const [options, setOptions] = useState([]);

  const { data: fetchedOptions = [] } = coreApi["queue-options"].useList(
    { queueId, parentId: -1 },
    {
      enabled: !!queueId,
      select: (data) =>
        (Array.isArray(data) ? data : []).map((option) => ({
          ...option,
          children: option.children || [],
          edition: false,
        })),
    }
  );

  useEffect(() => {
    if (!!queueId && fetchedOptions.length) {
      setOptions(fetchedOptions);
    }
  }, [queueId, fetchedOptions]);

  const updateOptions = useCallback((updater) => {
    setOptions((prev) => {
      const next = updater(prev || []);
      return next;
    });
  }, []);

  const addOption = useCallback(() => {
    setOptions((prev) => {
      const newOption = {
        title: "",
        message: "",
        edition: true,
        option: (prev || []).length + 1,
        queueId,
        parentId: null,
        children: [],
      };
      return [...(prev || []), newOption];
    });
  }, [queueId]);

  const handleAddChild = useCallback((pathToParent, childrenOrNull = null, opts = {}) => {
    if (!Array.isArray(pathToParent) || pathToParent.length === 0) {
      return;
    }
    updateOptions((prev) => {
      const parent = getNodeAtPath(prev, pathToParent);
      if (!parent) return prev;

      if (opts.replace && Array.isArray(childrenOrNull)) {
        return updateNodeAtPath(prev, pathToParent, (n) => ({ ...n, children: childrenOrNull }));
      }

      if (Array.isArray(childrenOrNull) && !opts.replace) {
        return updateNodeAtPath(prev, pathToParent, (n) => ({
          ...n,
          children: [...(n.children || []), ...childrenOrNull],
        }));
      }

      const newChild = {
        title: "",
        message: "",
        edition: true,
        option: (parent.children ? parent.children.length : 0) + 1,
        queueId,
        parentId: parent.id || null,
        children: [],
      };
      return updateNodeAtPath(prev, pathToParent, (n) => ({
        ...n,
        children: [...(n.children || []), newChild],
      }));
    });
  }, [queueId, updateOptions]);

  const handleToggleEdition = useCallback((pathToNode, value) => {
    updateOptions((prev) =>
      updateNodeAtPath(prev, pathToNode, (n) => ({ ...n, edition: typeof value === "boolean" ? value : !n.edition }))
    );
  }, [updateOptions]);

  const handleChangeField = useCallback((pathToNode, field, value) => {
    updateOptions((prev) =>
      updateNodeAtPath(prev, pathToNode, (n) => ({ ...n, [field]: value }))
    );
  }, [updateOptions]);

  const handleDelete = useCallback(async (pathToNode) => {
    const node = getNodeAtPath(options, pathToNode.slice(0));
    try {
      if (node && node.id) {
        await api.request({
          url: `/queue-options/${node.id}`,
          method: "DELETE",
        });
      }
    } catch (e) {
      toastError(e);
    }

    setOptions((prev) => {
      let next = removeNodeAtPath(prev, pathToNode);
      if (pathToNode.length === 1) {
        next = next.map((n, i) => ({ ...n, option: i + 1 }));
      } else {
        const parentPath = pathToNode.slice(0, -1);
        next = updateNodeAtPath(next, parentPath, (parent) => ({
          ...parent,
          children: (parent.children || []).map((c, i) => ({ ...c, option: i + 1 })),
        }));
      }
      return next;
    });
  }, [options]);

  const handleReplaceChildren = useCallback((parentPath, childrenArray) => {
    if (!Array.isArray(parentPath) || parentPath.length === 0) return;
    updateOptions((prev) => updateNodeAtPath(prev, parentPath, (n) => ({ ...n, children: childrenArray })));
  }, [updateOptions]);

  return (
    <div className={classes.root}>
      <br />
      <Typography>
        Opciones
        <Button
          color="primary"
          size="small"
          onClick={addOption}
          startIcon={<AddIcon />}
          style={{ marginLeft: 10 }}
          variant="outlined"
        >
          Añadir
        </Button>
      </Typography>

      {options.length > 0 ? (
        <QueueOptionStepper
          queueId={queueId}
          options={options}
          pathPrefix={[]}
          onAddChild={(path, children, opts) => handleAddChild(path, children, opts)}
          onToggleEdition={(path, value) => handleToggleEdition(path, value)}
          onChangeField={(path, field, value) => handleChangeField(path, field, value)}
          onDelete={(path) => handleDelete(path)}
          onReplaceChildren={(path, children) => handleReplaceChildren(path, children)}
        />
      ) : null}
    </div>
  );
}
